// Line selection: pool per trigger, no repeats within the last N lines, common triggers are
// sometimes silent. Uses the `dialogue` stream so it never perturbs reels or drafts.
import { B } from '../content/balance';
import { DIALOGUE, dialogueKeys, scriptKeys, type DialogueTrigger, type DialoguePool, type ScriptId } from '../content/dialogue';
import { stream } from './rng';
import type { Expression, GameEvent, GameState } from './types';

export function say(
  state: GameState,
  trigger: DialogueTrigger,
  events: GameEvent[],
  params?: Record<string, string | number>,
  opts: { force?: boolean; awake?: boolean } = {},
): void {
  const run = state.run;
  if (!run) return;
  const pool: DialoguePool = DIALOGUE[trigger];
  const awake = opts.awake ?? run.size !== 'bandit';
  const channel = awake ? 'display' : 'receipt';
  const keys = dialogueKeys(trigger, channel);
  if (keys.length === 0) return;
  const rng = stream(run.rng, 'dialogue');
  if (!opts.force && pool.common && rng.chance(B.DIALOGUE_SILENCE)) return;
  const recent = state.meta.recentLines;
  const fresh = keys.filter((k) => !recent.includes(k));
  const key = rng.pick(fresh.length ? fresh : keys);
  recent.push(key);
  while (recent.length > B.DIALOGUE_NO_REPEAT) recent.shift();
  events.push({ e: 'SAY', key, channel, expression: awake ? pool.expression : undefined, params });
}

/** A scripted speech: every line in order, always spoken (the display must be awake). */
export function sayScript(state: GameState, id: ScriptId, events: GameEvent[], expression: Expression = 'neutral', params?: Record<string, string | number>): void {
  if (!state.run || state.run.size === 'bandit') return;
  for (const key of scriptKeys(id)) events.push({ e: 'SAY', key, channel: 'display', expression, params });
}

export function tip(state: GameState, key: string, events: GameEvent[]): void {
  if (!state.meta.settings.tips || state.meta.tipsSeen.includes(key)) return;
  state.meta.tipsSeen.push(key);
  events.push({ e: 'TIP', key });
}

export type { DialogueTrigger };
export { DIALOGUE };
